/**
 * AnnotatablePdfPane — the Submittal Review package pane's markup layer (Submittal Review Plan
 * Phase 5, docs/SUBMITTAL_REVIEW_PLAN.md "Phase 5 — Markup, checklist runtime, and complete-review").
 *
 * A NEW sibling to PdfPane.jsx — not a modification of it. PdfPane stays the plain read-only
 * viewer used everywhere else in SubmittalReviewModal.jsx (Drawings pane, Specs pane); this
 * component is additive, used only for the submitted package pane, which is the one surface that
 * needs markup. It calls useRasterViewport.js directly (same hook PdfPane wraps) and layers a
 * second, absolutely-positioned canvas + a small toolbar on top for drawing.
 *
 * Coordinate model (Decision D7): every shape's points are stored as PAGE-RELATIVE VISUAL
 * FRACTIONS [fx, fy] via the hook's own screenToFrac/fracToScreen — never PDF user-space, never a
 * page_frame fingerprint. This is the deliberate departure from QcMarkupModal's model (see
 * useRasterViewport.js's header comment and docs/SUBMITTAL_REVIEW_PLAN.md).
 *
 * annotation_json shape (one object per file, per round): { pages: { [pageNum]: { shapes: [
 *   { id, type: 'pen'|'highlight'|'rect'|'arrow'|'text', points: [[fx,fy], ...], color,
 *     strokeWidth (fraction of page width), text? (type 'text' only), page, author, createdAt }
 * ] } } }
 *
 * Persistence: debounced autosave ~1.5s after the last edit, plus a flush on unmount. A 409 from
 * the server (the round's review has been posted — see api/submittal-review.js's freeze guard)
 * shows an inline banner and stops further autosave attempts until the pane is reloaded against a
 * different (editable) round — the parent already re-mounts this component with a fresh `key`
 * whenever the round/file selection changes, which is what "reloaded" means in practice here.
 *
 * `editable` false (viewing a past round, or the current round's lock is held by someone else, or
 * the round has already been posted) hides the toolbar entirely; shapes still render read-only.
 *
 * Text notes use `promptDialog` (Dialog.jsx) — never the browser's window.prompt, per CLAUDE.md.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRasterViewport } from './useRasterViewport.js'
import { promptDialog } from '../Dialog.jsx'
import { saveSubmittalMarkup } from '../../services/submittalReview.js'

const COLORS = ['#dc2626', '#d97706', '#1a6fa8', '#1D9E75', '#7c3aed', '#07272D']
const STROKE_FRAC = { pen: 0.0026, highlight: 0.016, rect: 0.0022, arrow: 0.0022 }
const FONT_FRAC = 0.016
const AUTOSAVE_MS = 1500
const MAX_UNDO = 50
const TOOLS = [
  { id: 'pan', label: '✋', title: 'Pan / select' },
  { id: 'pen', label: '✎', title: 'Pen' },
  { id: 'highlight', label: '▮', title: 'Highlighter' },
  { id: 'rect', label: '▢', title: 'Rectangle / cloud' },
  { id: 'arrow', label: '↗', title: 'Arrow' },
  { id: 'text', label: 'T', title: 'Text note' },
]

function newAnno() { return { pages: {} } }
function clonePage(anno, pageNum) {
  const p = anno.pages && anno.pages[pageNum]
  return p ? { shapes: (p.shapes || []).map(s => ({ ...s, points: (s.points || []).map(pt => [...pt]) })) } : { shapes: [] }
}
function withPageShapes(anno, pageNum, shapes) {
  return { ...anno, pages: { ...anno.pages, [pageNum]: { shapes } } }
}

function drawArrowhead(g, x, y, angle, size) {
  g.beginPath()
  g.moveTo(x, y)
  g.lineTo(x - size * Math.cos(angle - Math.PI / 7), y - size * Math.sin(angle - Math.PI / 7))
  g.lineTo(x - size * Math.cos(angle + Math.PI / 7), y - size * Math.sin(angle + Math.PI / 7))
  g.closePath()
  g.fill()
}

/** Rectangle with a scalloped "cloud" border — technique mirrored from QcMarkupModal's
 *  drawCloud (perimeter sample points at spacing r*1.6, an arc drawn at each). */
function drawCloudRect(g, x, y, w, h, r) {
  const per = []
  for (let p = x; p < x + w; p += r * 1.6) per.push([p, y])
  for (let p = y; p < y + h; p += r * 1.6) per.push([x + w, p])
  for (let p = x + w; p > x; p -= r * 1.6) per.push([p, y + h])
  for (let p = y + h; p > y; p -= r * 1.6) per.push([x, p])
  g.beginPath()
  per.forEach(p => g.arc(p[0], p[1], r, 0, Math.PI * 1.4))
  g.stroke()
}

export default function AnnotatablePdfPane({
  pdfUrl, title, extraToolbarContent,
  revisionId, fileId, initialAnnotation, editable, authorName,
}) {
  const {
    containerRef, canvasRef, loading, error,
    zoomIn, zoomOut, fitToWidth, beginPan, movePan, endPan,
    pageNum, numPages, goToPage, nextPage, prevPage,
    thumbnails, ensureThumbnails,
    screenToFrac, fracToScreen,
  } = useRasterViewport({ pdfUrl })

  const [railOpen, setRailOpen] = useState(false)
  const [tool, setTool] = useState('pan')
  const [color, setColor] = useState(COLORS[0])
  const [anno, setAnno] = useState(() => (initialAnnotation && initialAnnotation.pages) ? initialAnnotation : newAnno())
  const [frozen, setFrozen] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  const overlayRef = useRef(null)
  const draggingRef = useRef(false)
  const drawStateRef = useRef(null)     // in-progress gesture: { type, pts: [[sx,sy],...] }
  const annoRef = useRef(anno)
  annoRef.current = anno
  const pageNumRef = useRef(pageNum)
  pageNumRef.current = pageNum
  const undoStackRef = useRef([])       // [{ pageNum, prevPage }] — page-scoped snapshots
  const redoStackRef = useRef([])
  const saveTimerRef = useRef(null)
  const dirtyRef = useRef(false)
  const frozenRef = useRef(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (railOpen) ensureThumbnails() }, [railOpen, pdfUrl])

  // ── Persistence ──────────────────────────────────────────────────────────────
  const flush = useCallback(() => {
    if (!dirtyRef.current || frozenRef.current || !revisionId || !fileId) return
    dirtyRef.current = false
    saveSubmittalMarkup(revisionId, fileId, annoRef.current).catch(err => {
      if (err && err.status === 409) { frozenRef.current = true; setFrozen(true) }
      else setSaveError(err.message || 'Could not save markup')
    })
  }, [revisionId, fileId])

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(flush, AUTOSAVE_MS)
  }, [flush])

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); flush() }, [flush])

  // ── Undo / redo (page-scoped snapshots, cap MAX_UNDO) ─────────────────────────
  function pushUndo() {
    undoStackRef.current.push({ pageNum: pageNumRef.current, prevPage: clonePage(annoRef.current, pageNumRef.current) })
    if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()
    redoStackRef.current = []
    setCanUndo(true); setCanRedo(false)
  }
  function commitShape(shape) {
    pushUndo()
    const pn = pageNumRef.current
    const shapes = [...((annoRef.current.pages[pn] || {}).shapes || []), shape]
    setAnno(a => withPageShapes(a, pn, shapes))
    scheduleSave()
  }
  function undo() {
    const entry = undoStackRef.current.pop()
    if (!entry) return
    redoStackRef.current.push({ pageNum: entry.pageNum, prevPage: clonePage(annoRef.current, entry.pageNum) })
    setAnno(a => withPageShapes(a, entry.pageNum, entry.prevPage.shapes))
    setCanUndo(undoStackRef.current.length > 0); setCanRedo(true)
    scheduleSave()
  }
  function redo() {
    const entry = redoStackRef.current.pop()
    if (!entry) return
    undoStackRef.current.push({ pageNum: entry.pageNum, prevPage: clonePage(annoRef.current, entry.pageNum) })
    setAnno(a => withPageShapes(a, entry.pageNum, entry.prevPage.shapes))
    setCanUndo(true); setCanRedo(redoStackRef.current.length > 0)
    scheduleSave()
  }

  // ── Overlay redraw ─────────────────────────────────────────────────────────────
  function fracLenToScreenPx(len) {
    const a = fracToScreen(0, 0), b = fracToScreen(len, 0)
    return Math.max(0.5, b[0] - a[0])
  }
  function drawShape(g, s) {
    const pts = (s.points || []).map(p => fracToScreen(p[0], p[1]))
    if (!pts.length) return
    g.save()
    g.strokeStyle = s.color || '#07272D'
    g.fillStyle = s.color || '#07272D'
    g.lineCap = 'round'; g.lineJoin = 'round'
    const w = fracLenToScreenPx(s.strokeWidth || STROKE_FRAC.pen)
    g.lineWidth = w
    if (s.type === 'pen') {
      g.beginPath(); g.moveTo(pts[0][0], pts[0][1])
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1])
      g.stroke()
    } else if (s.type === 'highlight') {
      g.globalAlpha = 0.35
      g.beginPath(); g.moveTo(pts[0][0], pts[0][1])
      for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1])
      g.stroke()
      g.globalAlpha = 1
    } else if (s.type === 'rect' && pts.length >= 2) {
      const [x0, y0] = pts[0], [x1, y1] = pts[1]
      const x = Math.min(x0, x1), y = Math.min(y0, y1), w2 = Math.abs(x1 - x0), h2 = Math.abs(y1 - y0)
      drawCloudRect(g, x, y, w2, h2, Math.max(6, w * 3))
    } else if (s.type === 'arrow' && pts.length >= 2) {
      const [x0, y0] = pts[0], [x1, y1] = pts[1]
      g.beginPath(); g.moveTo(x0, y0); g.lineTo(x1, y1); g.stroke()
      drawArrowhead(g, x1, y1, Math.atan2(y1 - y0, x1 - x0), Math.max(10, w * 4))
    } else if (s.type === 'text') {
      const fontPx = fracLenToScreenPx(FONT_FRAC)
      g.font = '600 ' + fontPx.toFixed(1) + 'px "DM Sans",sans-serif'
      g.textBaseline = 'top'
      const lines = String(s.text || '').split('\n')
      const padding = 3
      const widest = Math.max(...lines.map(l => g.measureText(l).width))
      g.fillStyle = 'rgba(255,255,255,0.88)'
      g.fillRect(pts[0][0] - padding, pts[0][1] - padding, widest + padding * 2, lines.length * (fontPx * 1.25) + padding * 2)
      g.fillStyle = s.color || '#07272D'
      lines.forEach((l, i) => g.fillText(l, pts[0][0], pts[0][1] + i * fontPx * 1.25))
    }
    g.restore()
  }
  const renderOverlay = useCallback(() => {
    const canvas = overlayRef.current, cont = containerRef.current
    if (!canvas || !cont) return
    const dpr = window.devicePixelRatio || 1
    const cw = cont.clientWidth, ch = cont.clientHeight
    if (canvas.width !== Math.floor(cw * dpr) || canvas.height !== Math.floor(ch * dpr)) {
      canvas.width = Math.floor(cw * dpr); canvas.height = Math.floor(ch * dpr)
      canvas.style.width = cw + 'px'; canvas.style.height = ch + 'px'
    }
    const g = canvas.getContext('2d')
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    g.clearRect(0, 0, cw, ch)
    const shapes = ((annoRef.current.pages || {})[pageNumRef.current] || {}).shapes || []
    for (const s of shapes) drawShape(g, s)
    const ds = drawStateRef.current
    if (ds && ds.pts.length) {
      const preview = ds.type === 'pen' || ds.type === 'highlight'
        ? { type: ds.type, color: ds.color, strokeWidth: ds.strokeWidth, points: ds.pts.map(p => screenToFrac(p)) }
        : { type: ds.type, color: ds.color, strokeWidth: ds.strokeWidth, points: [ds.pts[0], ds.pts[ds.pts.length - 1]].map(p => screenToFrac(p)) }
      drawShape(g, preview)
    }
  }, [screenToFrac, fracToScreen]) // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw on every render (vp/page/anno changes all flow through React state) plus a
  // ResizeObserver so a sibling pane collapsing/expanding keeps the overlay in sync.
  useEffect(() => { renderOverlay() })
  useEffect(() => {
    const cont = containerRef.current
    if (!cont || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => renderOverlay())
    ro.observe(cont)
    return () => ro.disconnect()
  }, [renderOverlay]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pointer handling ───────────────────────────────────────────────────────────
  function localPoint(e) {
    const r = containerRef.current.getBoundingClientRect()
    return [e.clientX - r.left, e.clientY - r.top]
  }
  async function onPointerDown(e) {
    if (e.button !== 0 && e.button !== 1) return
    if (!editable || tool === 'pan') {
      draggingRef.current = true
      beginPan(e.clientX, e.clientY)
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
      return
    }
    const pt = localPoint(e)
    if (tool === 'text') {
      const text = await promptDialog({ title: 'Add text note', placeholder: 'Note text', multiline: true })
      if (text === null || !text.trim()) return
      commitShape({
        id: 'shp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7), type: 'text',
        points: [screenToFrac(pt)], color, strokeWidth: FONT_FRAC, text: text.trim(),
        page: pageNumRef.current, author: authorName || '', createdAt: new Date().toISOString(),
      })
      return
    }
    drawStateRef.current = { type: tool, color, strokeWidth: STROKE_FRAC[tool] || STROKE_FRAC.pen, pts: [pt] }
    draggingRef.current = true
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
    renderOverlay()
  }
  function onPointerMove(e) {
    if (!draggingRef.current) return
    if (!editable || tool === 'pan') { movePan(e.clientX, e.clientY); return }
    const ds = drawStateRef.current
    if (!ds) return
    const pt = localPoint(e)
    if (ds.type === 'pen' || ds.type === 'highlight') ds.pts.push(pt)
    else ds.pts[1] = pt
    renderOverlay()
  }
  function onPointerUp(e) {
    if (!draggingRef.current) return
    draggingRef.current = false
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
    if (!editable || tool === 'pan') { endPan(); return }
    const ds = drawStateRef.current
    drawStateRef.current = null
    if (!ds || ds.pts.length < 2) { renderOverlay(); return }
    const points = (ds.type === 'pen' || ds.type === 'highlight')
      ? ds.pts.map(p => screenToFrac(p))
      : [ds.pts[0], ds.pts[ds.pts.length - 1]].map(p => screenToFrac(p))
    commitShape({
      id: 'shp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7), type: ds.type,
      points, color: ds.color, strokeWidth: ds.strokeWidth,
      page: pageNumRef.current, author: authorName || '', createdAt: new Date().toISOString(),
    })
  }

  useEffect(() => {
    function onKey(e) {
      if (!editable) return
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo() }
      else if (mod && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={S.root}>
      <div style={S.toolbar}>
        {title != null && <span style={S.title} title={typeof title === 'string' ? title : undefined}>{title}</span>}
        <button type="button" style={S.btn} onClick={zoomOut} title="Zoom out">−</button>
        <button type="button" style={S.btn} onClick={zoomIn} title="Zoom in">+</button>
        <button type="button" style={S.btn} onClick={fitToWidth} title="Fit to width">Fit</button>
        {numPages > 1 && (
          <>
            <span style={S.divider} />
            <button type="button" style={S.btn} onClick={prevPage} disabled={pageNum <= 1} title="Previous page">‹</button>
            <span style={S.pageNum}>{pageNum} / {numPages}</span>
            <button type="button" style={S.btn} onClick={nextPage} disabled={pageNum >= numPages} title="Next page">›</button>
            <button type="button" style={{ ...S.btn, ...(railOpen ? S.btnActive : null) }} onClick={() => setRailOpen(o => !o)} title={railOpen ? 'Hide pages' : 'Show pages'}>▤</button>
          </>
        )}
        <div style={{ flex: 1 }} />
        {extraToolbarContent}
      </div>

      {editable && (
        <div style={S.annoBar}>
          {TOOLS.map(t => (
            <button key={t.id} type="button" title={t.title} onClick={() => setTool(t.id)}
              style={{ ...S.btn, ...(tool === t.id ? S.btnActive : null), fontSize: 13 }}>{t.label}</button>
          ))}
          <span style={S.divider} />
          {COLORS.map(c => (
            <button key={c} type="button" title={c} onClick={() => setColor(c)}
              style={{ ...S.swatch, background: c, boxShadow: color === c ? '0 0 0 2px var(--aj-accent)' : 'none' }} />
          ))}
          <span style={S.divider} />
          <button type="button" style={S.btn} onClick={undo} disabled={!canUndo} title="Undo (Ctrl/Cmd+Z)">↶</button>
          <button type="button" style={S.btn} onClick={redo} disabled={!canRedo} title="Redo (Ctrl/Cmd+Shift+Z)">↷</button>
        </div>
      )}

      {frozen && (
        <div style={S.frozenBanner}>This round's review has been posted — markup is locked. Reopen the tool to see the latest state.</div>
      )}
      {saveError && !frozen && <div style={S.frozenBanner}>Could not save markup: {saveError}</div>}

      <div style={S.body}>
        {railOpen && numPages > 1 && (
          <div style={S.rail}>
            {thumbnails.length === 0 && <div style={S.railHint}>Loading pages…</div>}
            {thumbnails.map(t => (
              <button key={t.pageNum} type="button" onClick={() => goToPage(t.pageNum)}
                style={{ ...S.thumbBtn, borderColor: t.pageNum === pageNum ? 'var(--aj-accent)' : 'var(--aj-border-mid)' }}>
                <img src={t.dataUrl} alt={'Page ' + t.pageNum} style={S.thumbImg} />
                <span style={S.thumbLbl}>{t.pageNum}</span>
              </button>
            ))}
          </div>
        )}
        <div
          ref={containerRef}
          style={{ ...S.canvasWrap, cursor: editable && tool !== 'pan' ? 'crosshair' : 'grab' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <canvas ref={canvasRef} style={S.canvas} />
          <canvas ref={overlayRef} style={{ ...S.canvas, pointerEvents: 'none' }} />
          {loading && <div style={S.overlay}>Loading…</div>}
          {!loading && error && <div style={{ ...S.overlay, color: '#dc2626' }}>{error}</div>}
          {!loading && !error && !pdfUrl && <div style={S.overlay}>No document</div>}
        </div>
      </div>
    </div>
  )
}

const S = {
  // flex:1, not height:'100%' — see PdfPane.jsx's identical comment (2026-08 fix).
  root: { display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, flex: 1, background: 'var(--aj-bg-surface)' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px',
    borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)', flexShrink: 0,
  },
  annoBar: {
    display: 'flex', alignItems: 'center', gap: 4, padding: '5px 8px',
    borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)', flexShrink: 0, flexWrap: 'wrap',
  },
  title: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)',
    marginRight: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200,
  },
  divider: { width: '0.5px', height: 18, background: 'var(--aj-border-mid)', margin: '0 2px', flexShrink: 0 },
  btn: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, lineHeight: 1,
    minWidth: 24, height: 24, padding: '0 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)',
    background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer',
  },
  btnActive: { background: 'var(--aj-bg-muted)', color: 'var(--aj-text)' },
  swatch: { width: 18, height: 18, borderRadius: '50%', border: '0.5px solid var(--aj-border-mid)', cursor: 'pointer', padding: 0 },
  pageNum: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)', padding: '0 4px', whiteSpace: 'nowrap' },
  frozenBanner: { padding: '5px 10px', background: '#3a2a10', color: '#f0c890', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, flexShrink: 0 },
  body: { flex: 1, minHeight: 0, display: 'flex', position: 'relative' },
  rail: {
    width: 96, flexShrink: 0, overflowY: 'auto', borderRight: '0.5px solid var(--aj-border)',
    background: 'var(--aj-bg-subtle)', padding: 6, display: 'flex', flexDirection: 'column', gap: 6,
  },
  railHint: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', textAlign: 'center', padding: '8px 0' },
  thumbBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 3,
    background: 'var(--aj-bg-elevated)', border: '2px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer',
  },
  thumbImg: { width: '100%', height: 'auto', display: 'block', border: '0.5px solid var(--aj-border)', borderRadius: 2 },
  thumbLbl: { fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' },
  canvasWrap: { flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden', background: 'var(--aj-bg-muted)', touchAction: 'none' },
  canvas: { position: 'absolute', top: 0, left: 0, display: 'block' },
  overlay: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)', textAlign: 'center', padding: 20,
    background: 'var(--aj-bg-muted)', pointerEvents: 'none',
  },
}
